import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ContentAsset, isDatamartField, ContentAssetRule, lookupComparators } from '@r1-compass-library/models';
import { ContentLibraryRuleForm } from '@r1-compass-library/models/content-library/content-library-rule-form.model';
import { ContentLibraryDefinitionForm } from '@r1-compass-library/models/content-library/content-library-definition-form.model';
import {
    ValidationService,
    ValidationStatus,
    ModelFormMap,
    ContentLibrarySectionName,
} from '@r1-compass-library/features/validation-sync';
import { typeValidator } from '@r1-compass-library/validators';
import { DisableValue1AndValue2Conditionally } from '@r1-compass-library/features/validation-sync/disable-value1-and-value2-conditionally.service';

export type ContentLibrarySectionFormMap = {
    definition: FormGroup<ContentLibraryDefinitionForm>;
    rules: FormGroup<ContentLibraryRuleForm>;
} & ModelFormMap<ContentLibrarySectionName>;

@Injectable({
    providedIn: 'root',
})
export class ContentLibraryValidationService
    implements ValidationService<ContentLibrarySectionName, ContentAsset, ContentLibrarySectionFormMap>
{
    validateModel(ca: ContentAsset): ValidationStatus<ContentLibrarySectionName> {
        const validationStatus: ValidationStatus<ContentLibrarySectionName> = { erroredSections: new Set() };

        if (this.isDefinitionInvalid(ca)) {
            validationStatus.erroredSections.add('definition');
        }
        if (this.isRulesInvalid(ca)) {
            validationStatus.erroredSections.add('rules');
        }
        return validationStatus;
    }

    validateSection<K extends ContentLibrarySectionName>(
        form: ContentLibrarySectionFormMap[K],
        section: K
    ): ValidationStatus<ContentLibrarySectionName> {
        const validationStatus: ValidationStatus<ContentLibrarySectionName> = { erroredSections: new Set() };
        if (form.invalid) {
            validationStatus.erroredSections.add(section);
        }
        return validationStatus;
    }

    private isDefinitionInvalid(contentAsset: ContentAsset): boolean {
        const definitionForm = new FormGroup<ContentLibraryDefinitionForm>(
            new ContentLibraryDefinitionForm({
                name: contentAsset.name,
                description: contentAsset.description ?? '',
                playImporterId: contentAsset.playImporterId ?? 0,
            })
        );

        return definitionForm.invalid;
    }

    private isRulesInvalid(contentAsset: ContentAsset): boolean {
        if (contentAsset.rules?.length === 0) {
            return true;
        }
        return (
            contentAsset.rules?.some(rule => {
                const ruleForm = this.buildRuleForm(rule, contentAsset.playImporterId);
                return ruleForm.invalid;
            }) ?? true
        );
    }

    public buildRuleForm(
        rule: ContentAssetRule,
        playImporterId: number | undefined
    ): FormGroup<ContentLibraryRuleForm> {
        const ruleForm = new FormGroup<ContentLibraryRuleForm>(new ContentLibraryRuleForm(rule));

        DisableValue1AndValue2Conditionally.disableContentLibrary(rule, ruleForm);
        this.setValidatorsForInputTypes(rule, ruleForm);

        // For existing configs with a play plus Datamart that need to be wiped to only allow re-saving with CSV values
        if (playImporterId && isDatamartField(rule.field)) {
            ruleForm.controls.fieldName.reset();
            ruleForm.controls.dataType.reset();
        }
        return ruleForm;
    }

    /**
     * Sets input type validators (ex: numeric, data) on the formcontrols Value1 and Value2
     */
    private setValidatorsForInputTypes(
        ruleData: ContentAssetRule,
        contentLibraryRuleForm: FormGroup<ContentLibraryRuleForm>
    ) {
        const dataMartField = ruleData.field;
        const comparator = contentLibraryRuleForm.controls.comparator.value;
        if (dataMartField) {
            const comparatorType = lookupComparators(dataMartField).find(
                comparatorMetadata => comparatorMetadata.value === comparator
            )?.valueType;

            const dataTypeIsNumeric =
                dataMartField.dataType != undefined &&
                ['integer', 'number', 'numeric'].includes(dataMartField.dataType.toLowerCase());

            if (comparatorType === 'date') {
                contentLibraryRuleForm.controls.value.addValidators(typeValidator('date'));
            } else if (comparatorType === 'timeInterval') {
                contentLibraryRuleForm.controls.value.addValidators(typeValidator('numeric'));
            } else if (comparatorType === 'text' && dataTypeIsNumeric) {
                contentLibraryRuleForm.controls.value.addValidators(typeValidator('numeric'));
            }
            contentLibraryRuleForm.controls.value.updateValueAndValidity();
        }
    }
}
